import Hex from './hex';

export default class Stroke {
	public get cap(): 'butt' | 'round' | 'square' {
		return this.#cap;
	}

	public get dash(): number {
		return this.#dash;
	}

	public get gap(): number {
		return this.#gap;
	}

	public get join(): 'bevel' | 'miter' | 'round' {
		return this.#join;
	}

	#cap: 'butt' | 'round' | 'square';

	#color: Hex;

	#dash: number;

	#gap: number;

	#join: 'bevel' | 'miter' | 'round';

	#position: 'center' | 'inside' | 'outside';

	#style: 'dashed' | 'solid';

	#weight: number;

	/**
	 * The stroke property is a presentation attribute defining the stroke used to paint the outline of the shape.
	 *
	 * @param color An instance of the Hex class
	 * @param weight The thickness of the stroke in pixels
	 * @param position The position of the stroke relative to the bounding box
	 * @param style Either solid or dashed
	 * @param join The shape to be used at the corners of paths when they are stroked
	 * @param cap The stroke ending shape
	 * @param gap Gap in pixels between the dashes if Stroke style is 'dashed'
	 * @param dash The dashes length in pixels if Stroke style is 'dashed'
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/stroke} for syntax
	 */
	public constructor(color: Hex, weight = 1, position: 'center' | 'inside' | 'outside' = 'outside', style: 'dashed' | 'solid' = 'solid', join: 'bevel' | 'miter' | 'round' = 'miter', cap: 'butt' | 'round' | 'square' = 'butt', gap = 0, dash = 2) {
		assertIsInstanceOfHex(color);
		this.#color = color;
		assertIsValidPosition(position);
		this.#position = position;
		assertIsValidStyle(style);
		this.#style = style;
		assertIsNonNegative(weight);
		this.#weight = weight;
		assertIsValidJoin(join);
		this.#join = join;
		assertIsValidCap(cap);
		this.#cap = cap;
		assertIsNonNegative(gap);
		this.#gap = gap;
		assertIsPositive(dash);
		this.#dash = dash;
	}

	/**
	 * Returns the outline offset as a 'Npx' string
	 *
	 * @returns String
	 */
	public toStyleOffsetString(): string {
		if (this.#position === 'center') {
			return `-${this.#weight * 0.5}px`;
		}
		if (this.#position === 'inside') {
			return `-${this.#weight}px`;
		}
		return '0px';
	}

	/**
	 * Return the Stroke value as a 'Npx style #RRGGBBAA' string
	 *
	 * @returns String
	 */
	public toStyleString(): string {
		if (this.#style === 'solid') {
			return `${this.#weight}px solid ${this.#color.toStyleString()}`;
		}
		return `${this.#weight}px dashed ${this.#color.toStyleString()}`;
	}
}

function assertIsInstanceOfHex(value: unknown): asserts value is Hex {
	if (value instanceof Hex) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be an instance of Hex`);
}

function assertIsNonNegative(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number`);
}

function assertIsPositive(value: unknown): asserts value is number {
	if (typeof value === 'number' && value > 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a positive number`);
}

function assertIsValidCap(value: unknown): asserts value is 'butt' | 'round' | 'square' {
	if (value === 'butt' || value === 'round' || value === 'square') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "butt", "round" or "square"`);
}

function assertIsValidJoin(value: unknown): asserts value is 'bevel' | 'miter' | 'round' {
	if (value === 'bevel' || value === 'miter' || value === 'round') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "bevel", "miter" or "round"`);
}

function assertIsValidPosition(value: unknown): asserts value is 'center' | 'inside' | 'outside' {
	if (value === 'center' || value === 'inside' || value === 'outside') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "center", "inside" or "outside"`);
}

function assertIsValidStyle(value: unknown): asserts value is 'dashed' | 'solid' {
	if (value === 'solid' || value === 'dashed') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "solid" or "dashed"`);
}
